import * as React from 'react';
import { fetchBidderDataIfNeeded, getAuthToken, isBidderLoaded, isUserLoggedIn } from '@/redux/modules/user';
import { useDispatch, useSelector } from 'react-redux';
import AppHeaderMeta from './AppHeaderMeta';
import Auth from './Auth';
import BackToTop from '@/components/BacktoTop/BackToTop';
import BannerOutdatedBrowser from '@/components/BannerOutdatedBrowser/BannerOutdatedBrowser';
import Cookies from 'universal-cookie';
import CurrencyExperimentContainer from '@/components/ExperimentSetup/CurrencyExperimentContainer';
import CustomToastContainer from '@/components/Toast/CustomToastContainer';
import DismissableGetMobileBanner from '@/components/DismissableGetMobileBanner/DismissableGetMobileBanner';
import DismissableGetWebNotificationsBanner from '@/components/DismissableGetWebNotificationsBanner/DismissableGetWebNotificationsBanner';
import FeedbackLink from '@/components/FeedbackLink/FeedbackLink';
import { fetchUpcomingBidderBiddingIfNeeded } from '@/redux/modules/bidderBiddingInfoUpcoming';
import { fetchUserVariantsIfNeeded } from '@/redux/modules/userVariantTest';
import Footer from '@/components/Footer/Footer';
import ForgotPasswordAction from '@/components/Modals/ForgotPassword/ForgotPasswordAction';
import FullStory from '@/components/FullStory/FullStory';
import GeoLocationSearchExperimentContainer from '@/components/ExperimentSetup/GeoLocationSearchExperimentContainer';
import { getBrowserDataFromUA } from '@/redux/modules/browser';
import { getPageSizeFromCookie } from '@/utils/pagination';

import GoogleTagManager from '@liveauctioneers/caterwaul-components/lib/GoogleTagManager/GoogleTagManager';
import Header from '@/components/Header/Header';
import { hydrateClientState } from '@/redux/modules/client';
import { isWhiteLabel } from '@/redux/modules/whiteLabel';
import Modals from '@/components/Modals/Modals';
import NewSafariSentinel from '@/components/NewSafariSentinel/NewSafariSentinel';
import NotificationCountTitle from '@/components/NotificationCountTitle/NotificationCountTitle';
import { parseQueryParams } from '@/utils/queryParams';
import PubnubBidderNotificationContainer from '@/components/PubnubContainer/PubnubBidderNotificationContainer';
import PubnubGlobalNotificationContainer from '@/components/PubnubContainer/PubnubGlobalNotificationContainer';
import SailthruInit from '@/components/Sailthru/SailthruInit';
import SegmentTracker from '@/components/Segment/SegmentTracker';
import Signifyd from '@/components/Signifyd/Signifyd';
import SignupAction from '@/components/Modals/SignUp/SignupAction';
import styled from 'styled-components';
import SuspendedBidderBanner from '@/components/SuspendedBidderBanner/SuspendedBidderBanner';
import TermsOfServiceBanner from '@/components/TermsOfServiceBanner/TermsOfServiceBanner';
import TrustBoxScript from '@/components/TrustBox/TrustBoxScript';
import { useHistory } from 'react-router';
import WindowSizeContainer from '@/components/WindowSizeContainer/WindowSizeContainer';
import 'react-toastify/dist/ReactToastify.css';

const cookie = new Cookies();

type Props = {
    children: React.ReactNode;
};

const App = ({ children }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [isClientHydrated, setClientHydrated] = React.useState(false);
    const authToken = useSelector(getAuthToken);
    const bidderIsLoaded = useSelector(isBidderLoaded);
    const { hideForTesting }: any = parseQueryParams(history.location.search);
    const userLoggedIn = useSelector(isUserLoggedIn);

    const { pathname } = history.location;
    const biddingConsole = pathname.includes('/console/');

    const hydrateClient = React.useCallback(async () => {
        const token = cookie.get('bidder-auth') || '';
        const dateLastShown = cookie.get('join-modal-last-seen') || '';
        const hasLoggedInBefore = Boolean(cookie.get('bidder-has-logged-in')) || Boolean(token);
        const pageSizeCookie = cookie.get('pagination-page-size') || 24;
        const pageSize = getPageSizeFromCookie(pageSizeCookie);

        const userAgent = navigator.userAgent || '';
        const browser = getBrowserDataFromUA(userAgent);

        const disableModalForTesting = biddingConsole || hideForTesting === 'true';

        await dispatch(
            hydrateClientState({
                browser,
                dateLastShown,
                disableModalForTesting,
                hasLoggedInBefore,
                pageSize,
                token,
            })
        );
    }, [dispatch, biddingConsole, hideForTesting]);

    React.useEffect(() => {
        if (!bidderIsLoaded && authToken) {
            dispatch(fetchBidderDataIfNeeded(authToken, false));
        }
    }, [dispatch, authToken, bidderIsLoaded]);

    React.useEffect(() => {
        hydrateClient().then(() => {
            setClientHydrated(true);
            return Promise.all([
                dispatch(fetchUpcomingBidderBiddingIfNeeded()),
                dispatch(fetchBidderDataIfNeeded(authToken)),
                dispatch(fetchUserVariantsIfNeeded()),
            ]);
        });

        return () => {
            window.onstorage = null;
        };
    }, [authToken, dispatch, hydrateClient]);

    const showHeaderAndFooter = !biddingConsole;

    return (
        <React.StrictMode>
            <Signifyd />
            <TrustBoxScript />
            <CurrencyExperimentContainer />
            <GeoLocationSearchExperimentContainer />
            <FullStory />
            <FeedbackLink />
            {userLoggedIn && <PubnubBidderNotificationContainer />}
            <TermsOfServiceBanner />
            <DismissableGetMobileBanner />
            <DismissableGetWebNotificationsBanner />
            <SuspendedBidderBanner />
            <CustomToastContainer />
            <PubnubGlobalNotificationContainer />
            <NotificationCountTitle /> {/** TODO: update this */}
            <NewSafariSentinel />
            <Auth />
            {isClientHydrated && <SignupAction />}
            {isClientHydrated && <ForgotPasswordAction />}
            <StyledApp>
                {isWhiteLabel && <GoogleTagManager gtmId="GTM-MGNHR3N" />}
                {isWhiteLabel && <GoogleTagManager gtmId="GTM-T68B3J7" />}
                <WindowSizeContainer /> {/** TODO: update this */}
                <AppHeaderMeta />
                <SailthruInit />
                <BannerOutdatedBrowser />
                {showHeaderAndFooter && <Header />}
                <StyledAppContent id="main">{children}</StyledAppContent>
                {showHeaderAndFooter && <Footer />}
                <Modals />
                <SegmentTracker />
                <BackToTop />
            </StyledApp>
        </React.StrictMode>
    );
};

export default React.memo(App);

const StyledApp = styled.div`
    height: 100%;
    width: 100%;
    position: relative;

    @media print {
        overflow: initial;
    }
`;

const StyledAppContent = styled.div`
    height: 100%;

    @media (min-width: 1240px) {
        margin: 0 auto;
    }

    @media print {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        margin-top: 0 !important;
    }
`;
